'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BinaryCalculator() {
  const t = useTranslations("tools.binaryCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Binary Operation State
  const [binary1, setBinary1] = useState('10101010');
  const [binary2, setBinary2] = useState('11001100');
  const [operator, setOperator] = useState('+');
  const [operationResult, setOperationResult] = useState<any>(null);
  const [operationError, setOperationError] = useState('');

  // Binary to Decimal State
  const [binaryInput, setBinaryInput] = useState('10101010');
  const [b2dResult, setB2dResult] = useState<any>(null);
  const [b2dError, setB2dError] = useState('');

  // Decimal to Binary State
  const [decimalInput, setDecimalInput] = useState('170');
  const [d2bResult, setD2bResult] = useState<any>(null);
  const [d2bError, setD2bError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const isValidBinary = (str: string): boolean => {
    return /^[01]+$/.test(str) && str.length > 0;
  };

  const binaryToDecimal = (binary: string): number => {
    return parseInt(binary, 2);
  };

  const decimalToBinary = (decimal: number): string => {
    return decimal.toString(2);
  };

  const handleOperationCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setOperationError('');

    const bin1 = binary1.trim();
    const bin2 = binary2.trim();

    if (!isValidBinary(bin1)) {
      setOperationError(t('errorInvalidBinary1'));
      return;
    }

    if (!isValidBinary(bin2)) {
      setOperationError(t('errorInvalidBinary2'));
      return;
    }

    const decimal1 = binaryToDecimal(bin1);
    const decimal2 = binaryToDecimal(bin2);
    let result: number;

    switch(operator) {
      case '+':
        result = decimal1 + decimal2;
        break;
      case '-':
        result = decimal1 - decimal2;
        if (result < 0) {
          setOperationError(t('errorNegativeResult'));
          return;
        }
        break;
      case '*':
        result = decimal1 * decimal2;
        break;
      case '/':
        if (decimal2 === 0) {
          setOperationError(t('errorDivideByZero'));
          return;
        }
        result = Math.floor(decimal1 / decimal2);
        break;
      default:
        result = 0;
    }

    const binaryResult = decimalToBinary(result);
    setOperationResult({ binary: binaryResult, decimal: result });
  };

  const handleB2DCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setB2dError('');

    const binary = binaryInput.trim();

    if (!isValidBinary(binary)) {
      setB2dError(t('errorInvalidBinary'));
      return;
    }

    const decimal = binaryToDecimal(binary);
    const binaryArray = binary.split('').reverse();
    const breakdown = [];

    for (let i = binaryArray.length - 1; i >= 0; i--) {
      const bit = binaryArray[i];
      const power = i;
      const value = parseInt(bit) * Math.pow(2, power);

      breakdown.push({
        position: i,
        power: Math.pow(2, power),
        bit: bit,
        value: value
      });
    }

    setB2dResult({ binary, decimal, breakdown });
  };

  const handleD2BCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setD2bError('');

    const decimal = parseInt(decimalInput.trim());

    if (isNaN(decimal) || decimal < 0) {
      setD2bError(t('errorInvalidDecimal'));
      return;
    }

    if (decimal > Number.MAX_SAFE_INTEGER) {
      setD2bError(t('errorNumberTooLarge'));
      return;
    }

    const binary = decimalToBinary(decimal);
    const steps = [];
    let num = decimal;
    let step = 1;

    if (num === 0) {
      steps.push({ step: 1, division: '0 ÷ 2', quotient: 0, remainder: 0 });
    } else {
      while (num > 0) {
        const quotient = Math.floor(num / 2);
        const remainder = num % 2;
        steps.push({
          step: step,
          division: `${num} ÷ 2`,
          quotient: quotient,
          remainder: remainder
        });
        num = quotient;
        step++;
      }
    }

    setD2bResult({ decimal, binary, steps });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const calculatorSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { color: '#2563eb', fontWeight: 600, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: '#475569', fontWeight: 500, marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const binaryInputWrapperStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' as const },
    { flexDirection: 'column' as const, alignItems: 'stretch' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const binaryInputStyle = getResponsiveStyle(
    {
      flex: 1,
      minWidth: '200px',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontFamily: "'Courier New', monospace",
      transition: 'all 0.3s ease'
    },
    { minWidth: '100%' },
    {},
    {}
  );

  const operatorSelectStyle = {
    padding: '0.75rem 1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', flexWrap: 'wrap' as const },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      background: '#64748b',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      marginTop: '1.5rem',
      borderLeft: '4px solid #22c55e'
    },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultsH4Style = {
    color: '#166534',
    fontSize: '1.1rem',
    marginBottom: '1rem',
    fontWeight: 600
  };

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem'
    },
    { padding: '0.75rem', flexDirection: 'column' as const, alignItems: 'flex-start' as const, gap: '0.5rem' },
    { padding: '1rem', flexDirection: 'row' as const },
    { padding: '1rem', flexDirection: 'row' as const }
  );

  const resultLabelStyle = {
    color: '#475569',
    fontWeight: 500
  };

  const resultValueStyle = getResponsiveStyle(
    { color: '#1e293b', fontWeight: 700, fontFamily: "'Courier New', monospace" },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const errorMessageStyle = {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626'
  };

  const conversionTableStyle = {
    marginTop: '1.5rem',
    overflowX: 'auto' as const
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const
  };

  const thTdStyle = getResponsiveStyle(
    { padding: '0.75rem', textAlign: 'left' as const, borderBottom: '1px solid #e2e8f0' },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const thStyle = {
    ...thTdStyle,
    background: '#f8fafc',
    color: '#475569',
    fontWeight: 600
  };

  const tdStyle = {
    ...thTdStyle,
    fontFamily: "'Courier New', monospace"
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    color: colors.primary,
    fontWeight: 600
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={calculatorSectionStyle}>
            <h3 style={h3Style}>⚡ {t('operationsTitle')}</h3>
            <form onSubmit={handleOperationCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('binaryCalculationLabel')}</label>
                <div style={binaryInputWrapperStyle}>
                  <input
                    type="text"
                    value={binary1}
                    onChange={(e) => setBinary1(e.target.value)}
                    style={binaryInputStyle}
                    placeholder="10101010"
                    pattern="[01]+"
                    maxLength={64}
                  />
                  <select
                    value={operator}
                    onChange={(e) => setOperator(e.target.value)}
                    style={operatorSelectStyle}
                  >
                    <option value="+">+</option>
                    <option value="-">−</option>
                    <option value="*">×</option>
                    <option value="/">÷</option>
                  </select>
                  <input
                    type="text"
                    value={binary2}
                    onChange={(e) => setBinary2(e.target.value)}
                    style={binaryInputStyle}
                    placeholder="11001100"
                    pattern="[01]+"
                    maxLength={64}
                  />
                </div>
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button
                  type="button"
                  style={clearBtnStyle}
                  onClick={() => {
                    setBinary1('10101010');
                    setBinary2('11001100');
                    setOperator('+');
                    setOperationResult(null);
                    setOperationError('');
                  }}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>
            {operationError && (
              <div style={errorMessageStyle}>{operationError}</div>
            )}
            {operationResult && (
              <div style={resultsStyle}>
                <h4 style={resultsH4Style}>📊 {t('resultTitle')}</h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('binaryResultLabel')}</span>
                  <span style={resultValueStyle}>{operationResult.binary}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('decimalEquivalentLabel')}</span>
                  <span style={resultValueStyle}>{operationResult.decimal}</span>
                </div>
              </div>
            )}
          </div>

          <div style={calculatorSectionStyle}>
            <h3 style={h3Style}>🔄 {t('b2dTitle')}</h3>
            <form onSubmit={handleB2DCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('binaryValueLabel')}</label>
                <input
                  type="text"
                  value={binaryInput}
                  onChange={(e) => setBinaryInput(e.target.value)}
                  style={binaryInputStyle}
                  placeholder="10101010"
                  pattern="[01]+"
                  maxLength={64}
                />
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('convertButton')}</button>
                <button
                  type="button"
                  style={clearBtnStyle}
                  onClick={() => {
                    setBinaryInput('10101010');
                    setB2dResult(null);
                    setB2dError('');
                  }}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>
            {b2dError && (
              <div style={errorMessageStyle}>{b2dError}</div>
            )}
            {b2dResult && (
              <div style={resultsStyle}>
                <h4 style={resultsH4Style}>📊 {t('conversionResultTitle')}</h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('binaryLabel')}</span>
                  <span style={resultValueStyle}>{b2dResult.binary}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('decimalLabel')}</span>
                  <span style={resultValueStyle}>{b2dResult.decimal}</span>
                </div>
                <div style={conversionTableStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t('positionLabel')}</th>
                        <th style={thStyle}>{t('powerLabel')}</th>
                        <th style={thStyle}>{t('bitLabel')}</th>
                        <th style={thStyle}>{t('valueLabel')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b2dResult.breakdown.map((item: any, index: number) => (
                        <tr key={index}>
                          <td style={tdStyle}>{item.position}</td>
                          <td style={tdStyle}>2<sup>{item.position}</sup> = {item.power}</td>
                          <td style={tdStyle}>{item.bit}</td>
                          <td style={tdStyle}>{item.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div style={calculatorSectionStyle}>
            <h3 style={h3Style}>🔄 {t('d2bTitle')}</h3>
            <form onSubmit={handleD2BCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('decimalValueLabel')}</label>
                <input
                  type="number"
                  value={decimalInput}
                  onChange={(e) => setDecimalInput(e.target.value)}
                  style={binaryInputStyle}
                  placeholder="170"
                  min="0"
                  max="9007199254740991"
                />
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('convertButton')}</button>
                <button
                  type="button"
                  style={clearBtnStyle}
                  onClick={() => {
                    setDecimalInput('170');
                    setD2bResult(null);
                    setD2bError('');
                  }}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>
            {d2bError && (
              <div style={errorMessageStyle}>{d2bError}</div>
            )}
            {d2bResult && (
              <div style={resultsStyle}>
                <h4 style={resultsH4Style}>📊 {t('conversionResultTitle')}</h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('decimalLabel')}</span>
                  <span style={resultValueStyle}>{d2bResult.decimal}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('binaryLabel')}</span>
                  <span style={resultValueStyle}>{d2bResult.binary}</span>
                </div>
                <div style={conversionTableStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t('stepLabel')}</th>
                        <th style={thStyle}>{t('divisionLabel')}</th>
                        <th style={thStyle}>{t('quotientLabel')}</th>
                        <th style={thStyle}>{t('remainderLabel')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d2bResult.steps.map((item: any, index: number) => (
                        <tr key={index}>
                          <td style={tdStyle}>{item.step}</td>
                          <td style={tdStyle}>{item.division}</td>
                          <td style={tdStyle}>{item.quotient}</td>
                          <td style={tdStyle}>{item.remainder}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
          <li style={listItemStyle}>{t('insight5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('whatIsBinaryTitle')}</h3>
        <p style={paragraphStyle}>{t('whatIsBinaryDescription')}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('binaryOperationsTitle')}</h3>
        <p style={paragraphStyle}>{t('binaryOperationsDescription')}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('conversionMethodsTitle')}</h3>
        <p style={paragraphStyle}>{t('conversionMethodsDescription')}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('practicalUsesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practicalUse1')}</li>
          <li style={listItemStyle}>{t('practicalUse2')}</li>
          <li style={listItemStyle}>{t('practicalUse3')}</li>
          <li style={listItemStyle}>{t('practicalUse4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://apps.apple.com/us/app/binary-calc/id301630595" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://apps.apple.com/ba/app/binary-calculator-sdg/id6741471372" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://binary-calculator-pro.en.softonic.com/android" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference3')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}